import Validatable from '@/validator/Validatable';
import ModelValidator from '@/validator/Validator';

import AbstractFormControl from './AbstractFormControl';
import FormControl from './FormControl';
import FormValidity from './FormValidity';
import ControlValidator from './validator/CustomValidator';
import FormControlValidator from './validator/FormControlValidator';
import FormGroupValidator from './validator/FormGroupValidator';

type ControlsRecord<T> = {
  [K in keyof T]?: AbstractFormControl<T[K]>;
};

class FormGroup<T> extends AbstractFormControl<T> {
  constructor(
    public readonly controls: ControlsRecord<T>,
    public readonly validators: ControlValidator<T>[] = [],
  ) {
    const value = {} as Record<keyof T, AbstractFormControl<T[keyof T]>>;
    for (const [k, control] of Object.entries<AbstractFormControl>(controls)) {
      value[k] = control.value;
    }
    super(value as T, validators);
  }

  public static buildFormGroupFromModel<M extends Validatable, K extends keyof M = keyof M>(
    model: M | null,
    keys: Array<K>,
    modelValidator: ModelValidator,
  ): FormGroup<Pick<M, K>> {
    const controls = {} as ControlsRecord<Pick<M, K>>;
    modelValidator.init(model);
    for (const key of keys) {
      const controlValidator = FormControlValidator.buildValidator<M[K]>(modelValidator, key.toString());
      controls[key.toString()] = new FormControl(model?.[key], [controlValidator]);
    }

    const groupValidator = FormGroupValidator.buildValidator(modelValidator);
    return new FormGroup(controls, [groupValidator]);
  }

  public get value(): T {
    const output: Record<string, AbstractFormControl> = {};
    for (const [k, v] of Object.entries<AbstractFormControl>(this.controls)) {
      output[k] = v.value;
    }
    return output as T;
  }

  public get pristine(): boolean {
    return Object.values<AbstractFormControl>(this.controls).every((control) => control.pristine);
  }

  public get dirty(): boolean {
    return Object.values<AbstractFormControl>(this.controls).some((control) => control.dirty);
  }

  public get valid(): boolean {
    return Object.values<AbstractFormControl>(this.controls).every((control) => control.valid);
  }

  public get invalid(): boolean {
    return Object.values<AbstractFormControl>(this.controls).some((control) => control.invalid);
  }

  public get pending(): boolean {
    return Object.values<AbstractFormControl>(this.controls).some((control) => control.pending);
  }

  public add(key: string, control: FormControl) {
    this.controls[key] = control;
  }

  public remove(key: string) {
    delete this.controls[key];
  }

  public set(value) {
    this.reset();
    this.update(value);
  }

  public update(value: Partial<T>) {
    for (const [key, control] of Object.entries<AbstractFormControl>(this.controls)) {
      control.update(value?.[key]);
    }
  }

  public reset(withInitialValue = false): void {
    for (const control of Object.values<AbstractFormControl>(this.controls)) {
      control.reset(withInitialValue);
    }
  }

  public async runValidators(): Promise<FormValidity> {
    this.validity = FormValidity.Pending;
    const selfValidity = await super.runValidators();
    const childrenValidity = await this.runChildrenValidators();
    this.validity =
      selfValidity === FormValidity.Valid && childrenValidity === FormValidity.Valid
        ? FormValidity.Valid
        : FormValidity.Invalid;
    return this.validity;
  }

  public applyChanges(entity: Validatable): void {
    for (const [key, control] of Object.entries<AbstractFormControl>(this.controls)) {
      entity[key] = control.value;
    }
  }

  private async runChildrenValidators() {
    const controlsArray = Object.values<AbstractFormControl>(this.controls);
    await Promise.allSettled(controlsArray.map((control) => control.runValidators()));
    return controlsArray.some((control) => control.invalid) ? FormValidity.Invalid : FormValidity.Valid;
  }

  public markAsPristine(): void {
    for (const control of Object.values<AbstractFormControl>(this.controls)) {
      control.markAsPristine();
    }
  }

  public markAsDirty(): void {
    for (const control of Object.values<AbstractFormControl>(this.controls)) {
      control.markAsDirty();
    }
  }
}

export default FormGroup;
